
























































import { Component, Inject, Watch, Mixins } from "vue-property-decorator";
import { mapState } from "vuex";

// Mixins
import RequestPromises from "@/mixins/requestPromises";
import HasValidation from "@/mixins/HasValidation";
import Context from "@/mixins/context";

// Enums
import { DataModules } from "@/store/modules/data";

// Types
import {
  IProvisioningProvider,
  IContractProductProvider,
  IContractProductProviderMethods
} from "@/models/providers/contractProduct";
import { IState } from "@/store";
import { IProvisionConfiguration } from "@/models/provisioning";

@Component({
  computed: {
    ...mapState({
      configurationsScope(state: IState, getters) {
        return getters[`data/${this.dataModule}/configurationsScope`];
      },
      providerConfigurations(state: IState, getters) {
        return getters[`data/listArray`]({
          storeModule: this.dataModule,
          scope: this.configurationsScope
        });
      }
    })
  }
})
export default class ContractProductFunctionFieldsModal extends Mixins(
  RequestPromises,
  HasValidation,
  Context
) {
  // State
  public configurationsScope!: string;
  public providerConfigurations!: IProvisionConfiguration[];

  // Inject
  @Inject({ from: "contractProductProvider" })
  public readonly $cProdData!: () => IContractProductProvider;

  @Inject({ from: "contractProductProviderMethods" })
  public readonly $cProdMethods!: IContractProductProviderMethods;

  @Inject({ from: "cProdProvProvider" })
  public readonly $provisioningData!: () => IProvisioningProvider;

  public dataModule: DataModules = DataModules.PROVISIONING_CONTRACT_PRODUCTS;

  public isLoading = true;
  public isReloading = false;
  public requestError = false;
  public isProcessing = false;

  public configurationId = "";

  public created() {
    this.loadData();
  }

  public async loadData() {
    try {
      await this.getProviderConfigurations(this.isReloading);
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
      this.requestError = true;
    }
    this.isLoading = false;
    this.isReloading = false;
  }

  public reloadData() {
    this.isReloading = true;
    this.loadData();
  }

  public getProviderConfigurations(ignoreStored = false) {
    return this.$store.dispatch(
      `data/${this.dataModule}/listProviderConfigurations`,
      {
        categoryId: this.blueprint.category_id,
        scope: this.configurationsScope,
        vm: this,
        ignoreStored
      }
    );
  }

  public get cProdData() {
    return this.$cProdData();
  }

  public get provisioningData() {
    return this.$provisioningData();
  }

  public getActiveCProdCount(id: IProvisionConfiguration["id"]) {
    const config = this.providerConfigurations.find(
      i => i.id === id
    ) as IProvisionConfiguration;

    const found = config?.contract_product_counts?.find(
      i => i.status_code === "contract_active"
    );

    return found?.count || 0;
  }

  public get configurationGroups() {
    const configurationGroups = [] as {
      name: string;
      configurations: {
        id: IProvisionConfiguration["id"];
        name: string;
      }[];
    }[];

    this.$_.forEach(this.providerConfigurations, configuration => {
      const index = this.$_.findIndex(configurationGroups, [
        "name",
        this.$_.get(configuration, "provider.name")
      ]);

      if (index === -1) {
        configurationGroups.push({
          name: this.$_.get(configuration, "provider.name"),
          configurations: [this.$_.pick(configuration, ["id", "name"])]
        });
        return;
      }

      configurationGroups[index].configurations.push(
        this.$_.pick(configuration, ["id", "name"])
      );
    });

    return configurationGroups;
  }

  public async onConfigurationChange() {
    try {
      this.isProcessing = true;

      await this.updateProductConfiguration();
      await this.$cProdMethods.loadContractData(true);

      this.$store.dispatch("toast/show", {
        message: this.$t("_.object_successfully_updated", {
          object: this.$t("_.configuration")
        })
      });
    } catch (error) {
      this.$store.dispatch("api/handleValidationError", {
        error,
        vm: this
      });
    }
    this.isProcessing = false;
  }

  public updateProductConfiguration() {
    return this.$store.dispatch(
      `data/${this.dataModule}/updateContractProductConfiguration`,
      {
        contractId: this.provisioningData.contractId,
        productId: this.provisioningData.productId,
        data: {
          configuration_id: this.configurationId
        }
      }
    );
  }

  public get contractProduct() {
    return this.$_.find(this.$_.get(this.cProdData, "contract.products"), [
      "id",
      this.provisioningData.productId
    ]);
  }

  public get blueprint() {
    return this.$_.get(this.contractProduct, "product.provision_blueprint");
  }

  public get provider() {
    return this.$_.get(this.contractProduct, "provision_provider");
  }

  public get configuration() {
    return this.$_.get(this.contractProduct, "provision_configuration");
  }

  @Watch("configuration")
  public configurationChanged() {
    this.configurationId = this.$_.get(this.configuration, "id", "");
  }

  public beforeDestroy() {
    this.$store.dispatch("data/binList", {
      storeModule: this.dataModule,
      scope: this.configurationsScope,
      vm: this
    });
  }
}
